// CONTRACT:C4-DESIGN-TOKENS.1.0
// COP — the operator AVATAR.
//
// Google-federated users get their Google profile PICTURE when the session carries one
// (the TDFLite IdP mints `name`/`picture` into the access token on the federation grant;
// host/tdflite.ts rides them on Session and preserves them across refresh). Everyone else
// (and any broken picture URL) gets a DETERMINISTIC initials disc: hash(username) picks a
// colour from a curated muted palette — stable across sessions and devices, never neon —
// with FI+LI initials from the display name ("Will Ackerly" → "WA"; a single-word callsign
// takes its first two characters).
//
// DISPLAY-ONLY: the avatar is a label, never a security surface. The username in the JWT
// remains the ABAC subject regardless of what this renders.

import { useState } from "react";
import "./avatar.css";

/** Curated initials-disc palette — muted, dark-shell-friendly mid tones (deliberately NOT
 *  saturated primaries) with one shared light ink. Order is part of the determinism: a
 *  reorder re-colours every user, so append-only. */
const PALETTE: readonly { bg: string; fg: string }[] = [
  { bg: "#46698c", fg: "#ebf2f4" }, // steel blue
  { bg: "#3d7d7d", fg: "#ebf2f4" }, // teal
  { bg: "#567a4e", fg: "#ebf2f4" }, // moss
  { bg: "#8f6f3d", fg: "#ebf2f4" }, // ochre
  { bg: "#96543f", fg: "#ebf2f4" }, // terracotta
  { bg: "#7a5d96", fg: "#ebf2f4" }, // plum
  { bg: "#5d6b7a", fg: "#ebf2f4" }, // slate
  { bg: "#7d7d46", fg: "#ebf2f4" }, // olive
  { bg: "#96546b", fg: "#ebf2f4" }, // rose
  { bg: "#5d64a0", fg: "#ebf2f4" }, // indigo
];

/** FNV-1a 32-bit over the username — cheap, stable, well-spread for short strings. */
function fnv1a(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** The deterministic disc colour for a username (exported so other identity surfaces —
 *  e.g. a future map own-marker ring — can match the avatar colour exactly). */
export function avatarColorFor(username: string): { bg: string; fg: string } {
  return PALETTE[fnv1a(username) % PALETTE.length];
}

/** FI+LI initials from a display name: first char of the first + last word ("Will
 *  Ackerly" → "WA", "sarah.chen" → "SC"); a single word takes its first two characters
 *  ("VIPER" → "VI"). Code-point aware (no surrogate tearing). Empty → "?". */
export function initialsFor(name: string): string {
  const parts = name.trim().split(/[\s._-]+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = [...parts[0]];
  if (parts.length === 1) return first.slice(0, 2).join("").toUpperCase();
  const last = [...parts[parts.length - 1]];
  return `${first[0]}${last[0]}`.toUpperCase();
}

export interface AvatarProps {
  /** The stable identity key the disc colour derives from (the login username). */
  username: string;
  /** The name initials derive from (callsign / real name). Falls back to username. */
  displayName?: string;
  /** Google profile picture URL (federation logins). Used when present + loadable;
   *  a failed load falls back to the initials disc (never a broken-image glyph). */
  picture?: string;
  /** Disc diameter in px. Default 24. */
  size?: number;
}

/** The avatar: a deterministic initials disc that a Google profile PICTURE paints OVER when one
 *  is present + loadable. The initials disc is ALWAYS the base layer — so a picture that is slow,
 *  blocked (CSP / content-blocker), transparent, or fails WITHOUT firing an `error` event never
 *  leaves a near-background-coloured void (an all-but-invisible disc on the dark shell): the
 *  coloured initials show through until a real photo paints over them (opaque, object-fit:cover),
 *  and a hard load error drops the <img> for good. Marked aria-hidden — it always accompanies a
 *  text label; callers carry the accessible name. */
export function Avatar({ username, displayName, picture, size = 24 }: AvatarProps): JSX.Element {
  const [broken, setBroken] = useState(false);
  const label = (displayName ?? "").trim() || username;
  const { bg, fg } = avatarColorFor(username);
  const showImg = Boolean(picture) && !broken;

  return (
    <span
      className="avatar"
      style={{
        width: size,
        height: size,
        background: bg,
        color: fg,
        fontSize: Math.max(9, Math.round(size * 0.42)),
      }}
      aria-hidden="true"
    >
      <span className="avatar__initials">{initialsFor(label)}</span>
      {showImg && (
        <img
          className="avatar__img"
          src={picture}
          alt=""
          referrerPolicy="no-referrer"
          onError={() => setBroken(true)}
        />
      )}
    </span>
  );
}
